import axios from 'axios';
import Feathers from 'feathers/client';
import rest from 'feathers-rest/client';
import { CookieStorage } from 'cookie-storage';

const V1_TOKEN_KEY = 'RL6Token';
const A_MONTH_FROM_TODAY = new Date(Date.now() + 2592000000);
const cookieStorage = new CookieStorage({
  expires: A_MONTH_FROM_TODAY,
  path: '/',
});

// Helper function to check if current route is sunset-admin (standalone page)
function isSunsetAdminRoute() {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/sunset-admin');
}

const tokenGenerator = {
  getTokenPromise: null,
  token: null,
  async getToken() {
    if (!this.getTokenPromise) {
      this.getTokenPromise = new Promise(async (resolve, reject) => {
        if (!this.token) {
          try {
            const res = await axios
              .get(`${process.env.VUE_APP_AUTHENTICATION_API_BASE_URL}/refresh-token`, {
                withCredentials: true,
              });
            this.token = res.data.access_token;
            resolve(this.token);
          } catch (err) {
            reject(err);
          }
        } else {
          resolve(this.token);
        }
      });
    }
    return this.getTokenPromise;
  },
};

axios.interceptors.response.use(null, async ({ response }) => {
  // Skip all authentication handling for sunset-admin routes (standalone page)
  if (isSunsetAdminRoute()) {
    return Promise.reject(response);
  }
  
  if (
    response
      && response.config
      && response.config.url === `${process.env.VUE_APP_AUTHENTICATION_API_BASE_URL}/refresh-token`
  ) {
    cookieStorage.removeItem(V1_TOKEN_KEY);
    const redirect = encodeURIComponent(window.location.href);
    const newLocation = `${process.env.VUE_APP_ACCOUNTS_APP_URL}/signin?ref=${redirect}`;
    window.location.assign(newLocation);
  }
  // Handle 401 errors with token refresh
  if (response && response.status === 401 && !response.config._retry && !response.config.skipAuthInterceptor) {
    try {
      const token = await tokenGenerator.getToken();
      if (!token) return Promise.reject(response);
      cookieStorage.setItem('LD_AUTH', token, {
        domain: process.env.VUE_APP_MAIN_DOMAIN,
      });
      response.config._retry = true;
      tokenGenerator.token = null;
      tokenGenerator.getTokenPromise = null;
      return axios.request(response.config);
    } catch (err) {
      tokenGenerator.token = null;
      tokenGenerator.getTokenPromise = null;
      return err;
    }
  }
  return Promise.reject(response);
});

axios.interceptors.request.use(
  config => {
    if (config.skipAuthInterceptor) return config;

    // Don't add auth headers for sunset-admin routes (standalone page)
    if (isSunsetAdminRoute()) {
      return config;
    }

    config.headers.authorization = `Bearer ${cookieStorage.getItem('LD_AUTH')}`;
    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

axios.defaults.headers.common['X-Requested-From'] = 'LionDesk-CRM';
axios.defaults.headers.common['x-device'] = 'browser';
axios.defaults.headers.common['x-liondesk-dev'] = 'true';

const feathers = Feathers().configure(rest(process.env.VUE_APP_API_BASE_URL).axios(axios));

export { feathers };
